import { type VivaPlanungRouter } from '@backend-types/AppRouter';
import { type inferRouterInputs, type inferRouterOutputs } from '@trpc/server';
import { z } from 'zod';

/**
 * This file provides the backend TRPC types to the frontend.
 * Since TRPC only provides types but no enums (objects), we redefine them here manually.
 * If you have a better idea, feel free to improve this part.
 */

/*
 * Helpers
 */

type RouterInput = inferRouterInputs<VivaPlanungRouter>;
type RouterOutput = inferRouterOutputs<VivaPlanungRouter>;

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[]
  ? ElementType
  : never;

type NonNullable<T> = Exclude<T, null | undefined>;

// Ensures every member of TUnion appears as a value in TRecord, and only TUnion values are allowed. Keys may be any string.
function exhaustiveRecord<TUnion extends string>() {
  return <const TRecord extends Record<string, TUnion>>(
    record: MissingValues<TUnion, TRecord>,
  ): MissingValues<TUnion, TRecord> => record;
}

// Improve typescript error message for exhaustiveRecord
type MissingValues<TUnion extends string, TRecord extends Record<string, TUnion>> =
  Exclude<TUnion, TRecord[keyof TRecord]> extends never
    ? TRecord
    : `Missing values: ${Exclude<TUnion, TRecord[keyof TRecord]>}`;

/*
 * Router
 */

// search

export type SearchThemaInput = RouterInput['search']['searchThema'];
export type SearchThemaOutput = RouterOutput['search']['searchThema'];

export type SearchVeranstaltungOutput = RouterOutput['search']['searchVeranstaltungen'];
export type SearchVeranstaltungOutputItem = ArrayElement<SearchVeranstaltungOutput>;

export type ThemaFromSearchRouter = ArrayElement<SearchThemaOutput>;

// thema

export type GetThemaByAkaThemaIdInput = RouterInput['thema']['getThemaByAkaThemaId'];
export type GetThemaByAkaThemaIdOutput = RouterOutput['thema']['getThemaByAkaThemaId'];
export type GetThemaByAkaProduktIdInput = RouterInput['thema']['getThemaByAkaProduktId'];
export type GetThemaByAkaProduktIdOutput = RouterOutput['thema']['getThemaByAkaProduktId'];
export type GetTasksByAkaProduktIdInput = RouterInput['thema']['getTasksByAkaProduktId'];
export type GetTasksByAkaProduktIdOutput = RouterOutput['thema']['getTasksByAkaProduktId'];

export type ThemaFromThemaRouter = GetThemaByAkaThemaIdOutput['thema'];
export type StrukturellesFormat = ThemaFromThemaRouter['strukturellesFormat'];
export type PlanungsFormat = ThemaFromThemaRouter['planungsformat'];
export type AkaThemaId = ThemaFromThemaRouter['akaThemaId'];

export const planungsformate = exhaustiveRecord<PlanungsFormat>()({
  EINZELTERMIN: 'einzelTermin',
  TERMINGRUPPE: 'terminGruppe',
});
export type PlanungsinformationFromThemaRouter = NonNullable<ThemaFromThemaRouter['planungsinformation']>;
export type ModulFromThemaRouter = ArrayElement<NonNullable<ThemaFromThemaRouter['module']>>;
export type ZeitlicherAblauf = PlanungsinformationFromThemaRouter['zeitlicherAblauf'];
export type Sitzordnung = NonNullable<PlanungsinformationFromThemaRouter['sitzordnung']>;
export type Dauer = NonNullable<PlanungsinformationFromThemaRouter['dauer']>;
export type Experteplanung = ArrayElement<NonNullable<PlanungsinformationFromThemaRouter['expertePool']>>;

export type TaskListItem = ArrayElement<GetTasksByAkaProduktIdOutput>;
export type TaskStatus = TaskListItem['taskStatus'];
export type TaskType = TaskListItem['taskType'];

export const taskStatus = exhaustiveRecord<TaskStatus>()({
  ANGELEGT: 'angelegt',
  IN_ARBEIT: 'in arbeit',
  WARTEND: 'wartend',
  ABGESCHLOSSEN: 'abgeschlossen',
});

export const taskTypes = exhaustiveRecord<TaskType>()({
  VERANSTALTUNGEN_UMPLANEN: 'Veranstaltungen umplanen',
  VERANSTALTUNGEN_FREIGEBEN_UND_PLANUNG_IN_CSERV_ABSCHLIESSEN:
    'Veranstaltungen freigeben und Planung in CServ abschließen',
  TRAINER_ANMAHNEN: 'Trainer anmahnen',
  ERSATZTERMINE_ÜBERWACHEN: 'Ersatztermine überwachen',
  RÜCKMELDUNGEN_AUF_AUSSCHREIBUNG_BEARBEITEN: 'Rückmeldungen auf Ausschreibung bearbeiten',
  RÜCKMELDUNGEN_AUF_DAS_ANGEBOT_BEARBEITEN: 'Rückmeldungen auf das Angebot bearbeiten',
  MANUELLEN_AUSSCHREIBUNGSPROZESS_ÜBERWACHEN: 'Manuellen Ausschreibungsprozess überwachen',
  MANUELLER_TASK: 'Manueller Task',
});

export type Clusterzuordnung = NonNullable<Experteplanung['clusterzuordnung']>;

export const sitzordnung = exhaustiveRecord<Sitzordnung>()({
  FISCHGRAET: 'FISCHGRAET',
  KINOBESTUHLUNG: 'KINOBESTUHLUNG',
  OHNETISCHE: 'OHNETISCHE',
  PARLAMENTARISCH: 'PARLAMENTARISCH',
  RAUMABHAENGIG: 'RAUMABHAENGIG',
  RAUMSKIZZE: 'RAUMSKIZZE',
  STUHLHALBKREIS: 'STUHLHALBKREIS',
  STUHLKREIS: 'STUHLKREIS',
  TEILNEHMERABHAENGIG: 'TEILNEHMERABHAENGIG',
  UFORM: 'UFORM',
  UFORMBREITETISCHE: 'UFORMBREITETISCHE',
  ZWEIERTISCHGRUPPE: 'ZWEIERTISCHGRUPPE',
  VIERERTISCHGRUPPE: 'VIERERTISCHGRUPPE',
  SECHSERTISCHGRUPPE: 'SECHSERTISCHGRUPPE',
  TISCHBLOCK: 'TISCHBLOCK',
  PARLAMENTARISCHMITPC: 'PARLAMENTARISCHMITPC',
  ONLINE: 'ONLINE',
});

export const clusterzuordnung = exhaustiveRecord<Clusterzuordnung>()({
  HAUPTTRAINER: 'haupttrainer', // cluster A
  NEBENTRAINER: 'nebentrainer', // cluster B
  ERSATZTRAINER: 'ersatztrainer', // cluster C
  WEITERER_TRAINER: 'weitererTrainer', // cluster D
});

export type ExperteFromThemaRouter = NonNullable<Experteplanung['experte']>;
export type CservFormat = ThemaFromThemaRouter['strukturellesFormat'];

export const cservFormate = {
  SEMINAR_STANDARD: 551, // war: STANDARD_SEMINAR
  ONLINE_KURS_VC_MIT_MODULEN: 569, // war: ONLINE_KURS
  GENERAL_MANAGEMENT: 559,
  QUALIFIZIERUNGSPROGRAMM: 555,
  SOFTWARE_SCHULUNG: 554,
  PRUEFUNG_PRAESENZ_VA: 564,
  SCHULUNG: 552,
  BLENDED_LEARNING: 576, // war: AVENDOO_FORMAT
} as const satisfies Record<string, CservFormat>;

export type CservModulFormat = ModulFromThemaRouter['modulFormat'];

export const cservModulFormate = exhaustiveRecord<CservModulFormat>()({
  PRAESENZ_SEMINAR: 'praesenz_seminar',
  PRAESENZ_TRAINING: 'praesenz_training',
  E_LEARNING: 'eLearning',
  LERNIMPULS: 'lernimpuls',
  ONLINE_SEMINAR: 'online_seminar',
  ONLINE_TRAINING: 'online_training',
  WEBINAR: 'webinar',
  COACHING: 'coaching',
  SELBSTLERNPHASE: 'selbstlernphase',
  SELBSTLERNPHASE_MIT_WEBINAR: 'selbstlernphase_mit_webinar',
  PRUEFUNG: 'pruefung',
  EPRUEFUNG: 'epruefung',
  OPT_PREUFUNG: 'opt_preufung',
});

// veranstaltung
export type GetVeranstaltungByAkaVeranstaltungIdInput =
  RouterInput['veranstaltung']['getVeranstaltungByAkaVeranstaltungId'];
type GetVeranstaltungByAkaVeranstaltungIdOutput = RouterOutput['veranstaltung']['getVeranstaltungByAkaVeranstaltungId'];

export type Veranstaltung = GetVeranstaltungByAkaVeranstaltungIdOutput;

export type AkaVeranstaltungId = GetVeranstaltungByAkaVeranstaltungIdOutput['akaVeranstaltungId'];

// teilleistung
export type Teilleistung = Veranstaltung['teilleistungen'][number];
export type Leistung = Teilleistung['leistung'];

// ausschreibung
export type Ausschreibung = NonNullable<Leistung['ausschreibung']>;

export type AusschreibungStatus = Ausschreibung['status'] | 'keineAusschreibung';

export type ExperteAntwort = Ausschreibung['experteAntworten'][number];

export type Alternativvorschlag = ExperteAntwort['alternativvorschlaege'][number];

export type Antwort = ExperteAntwort['antwort'] | 'keineAntwort';

export const ausschreibungStatus = exhaustiveRecord<AusschreibungStatus>()({
  AUSSCHREIBUNGSENTWURF_GELOESCHT: 'ausschreibungsentwurfGeloescht',
  KEINE_AUSSCHREIBUNG: 'keineAusschreibung',
  AUSSCHREIBUNG_ABGEBROCHEN: 'ausschreibungAbgebrochen',
  AUSSCHREIBUNG_VEROEFFENTLICHT: 'ausschreibungVeroeffentlicht',
  AUSSCHREIBUNG_BEENDET: 'ausschreibungBeendet',
  ANGEBOT_VEROEFFENTLICHT: 'angebotVeroeffentlicht',
  ANGEBOT_BEENDET: 'angebotBeendet',
  VERTRAG_STORNIERT: 'vertragStorniert',
});

export const experteAntworten = exhaustiveRecord<Antwort>()({
  KEINE_ANTWORT: 'keineAntwort',
  AUSSCHREIBUNG_ABGELEHNT: 'ausschreibungAbgelehnt',
  AUSSCHREIBUNG_ABGELAUFEN: 'ausschreibungAbgelaufen',
  AUSSCHREIBUNG_UNTER_VORBEHALT_ANGENOMMEN: 'ausschreibungUnterVorbehaltAngenommen',
  AUSSCHREIBUNG_ANGENOMMEN: 'ausschreibungAngenommen',
  ANGEBOT_ABGELEHNT: 'angebotAbgelehnt',
  ANGEBOT_ABGELAUFEN: 'angebotAbgelaufen',
  ANGEBOT_ANGENOMMEN: 'angebotAngenommen',
});

export type ExperteBlockung = ArrayElement<Veranstaltung['experteBlockungen']>;
export type ExperteTermin = ArrayElement<Veranstaltung['experteTermine']>;
export type RaumBlockung = ArrayElement<Veranstaltung['raumBlockungen']>;
export type Zeitraeume = Veranstaltung['ablauf'];
export type Zeitraum = ArrayElement<Zeitraeume>;

export type HiveExperteTerminStatus = NonNullable<ExperteTermin['status']>;
export const hiveExperteTerminStatus = exhaustiveRecord<HiveExperteTerminStatus>()({
  GEPLANT: 'GEPLANT',
  GEBUCHT: 'GEBUCHT',
});

export type ExperteHiveTerminanfrageConflict = ArrayElement<NonNullable<ExperteTermin['konflikte']>>;

export type Geschaeftsbereich = GetVeranstaltungByAkaVeranstaltungIdOutput['geschaeftsbereich'];

export const geschaeftsbereiche = exhaustiveRecord<Geschaeftsbereich>()({
  CAS: 'CAS',
  DLS: 'DLS',
  ILS: 'ILS',
  IHC: 'IHC',
  KFF: 'KFF',
  UNKNOWN: 'UNKNOWN',
});

export type VivaStatus = NonNullable<GetVeranstaltungByAkaVeranstaltungIdOutput['vivaStatus']>;

export const vivaStatus = exhaustiveRecord<VivaStatus>()({
  IN_PLANUNG: 'inPlanung',
  IM_AUSSCHREIBUNGSPORTAL: 'imAusschreibungsportal',
  ABGESCHLOSSEN: 'abgeschlossen',
  FREIGEGEBEN: 'freigegeben',
});

export type SapStatus = NonNullable<GetVeranstaltungByAkaVeranstaltungIdOutput['sapStatus']>;
export type VivaOrSapStatus = VivaStatus | SapStatus;

export const sapStatus = exhaustiveRecord<SapStatus>()({
  ABGESAGT: 'abgesagt',
  FIXIERT: 'fixiert',
  GEPLANT: 'geplant',
  GESPERRT: 'gesperrt',
});

export type Ablauf = GetVeranstaltungByAkaVeranstaltungIdOutput['ablauf'];
export type AblaufTag = ArrayElement<Ablauf>;

export type Terminart = GetVeranstaltungByAkaVeranstaltungIdOutput['terminart'];

export const terminart = exhaustiveRecord<NonNullable<Terminart>>()({
  STANDARD: 'standard',
  ZUSATZ: 'zusatz',
  ERSATZ: 'ersatz',
});

export type GetVeranstaltungenByAkaProduktIdsInput = RouterInput['veranstaltung']['getVeranstaltungenByAkaProduktIds'];
export type GetVeranstaltungenByAkaProduktIdsOutput =
  RouterOutput['veranstaltung']['getVeranstaltungenByAkaProduktIds'];

export type CreateVeranstaltungInput = RouterInput['veranstaltung']['createVeranstaltung'];
export type CreateVeranstaltungOutput = RouterOutput['veranstaltung']['createVeranstaltung'];

export type UpdateAblaufInput = RouterInput['veranstaltung']['updateAblauf'];
export type UpdateAblaufOutput = RouterOutput['veranstaltung']['updateAblauf'];

export type UpdateRessourcenBlockungenOfVeranstaltungInput =
  RouterInput['veranstaltung']['updateRessourcenBlockungenOfVeranstaltung'];
export type UpdateRessourcenBlockungenOfVeranstaltungOutput =
  RouterOutput['veranstaltung']['updateRessourcenBlockungenOfVeranstaltung'];

export type RessourcenBlockungen = UpdateRessourcenBlockungenOfVeranstaltungInput['blockungen'];
export type RessourcenBlockung = ArrayElement<RessourcenBlockungen>;
export type RessourcenBlockungType = RessourcenBlockung['type'];

export const ressourcenBlockungTypes = exhaustiveRecord<RessourcenBlockungType>()({
  EXPERTE: 'experte',
  RAUM: 'raum',
  STANDORT: 'standort',
});

export type GetHealthByVeranstaltungInput = RouterInput['veranstaltung']['getHealthByVeranstaltung'];
export type GetHealthByVeranstaltungOutput = RouterOutput['veranstaltung']['getHealthByVeranstaltung'];

export type CheckHealthResult = ArrayElement<GetHealthByVeranstaltungOutput>;

export type HealthLevel = CheckHealthResult['type'];

export const healthLevel = exhaustiveRecord<HealthLevel>()({
  ERROR: 'error',
  WARNING: 'warning',
});

export type DeleteVeranstaltungenInput = RouterInput['veranstaltung']['deleteVeranstaltungenByAkaVeranstaltungId'];
export type DeleteVeranstaltungenOutput = RouterOutput['veranstaltung']['deleteVeranstaltungenByAkaVeranstaltungId'];

// Durchgang
export type CreateDurchgangInput = RouterInput['durchgang']['createDurchgang'];
export type CreateDurchgangInputBlockung = ArrayElement<
  ArrayElement<CreateDurchgangInput['modultermine']>['blockungen']
>;
export type CreateDurchgangOutput = RouterOutput['durchgang']['createDurchgang'];

export type GetDurchgangByAkaDurchgangIdInput = RouterInput['durchgang']['getDurchgangByAkaDurchgangId'];
export type GetDurchgangByAkaDurchgangIdOutput = RouterOutput['durchgang']['getDurchgangByAkaDurchgangId'];
export type GetDurchgangByAkaDurchgangIdModultermin = GetDurchgangByAkaDurchgangIdOutput['modultermine'][number];

export type GetDurchgaengeByAkaProduktIdsInput = RouterInput['durchgang']['getDurchgaengeByAkaProduktIds'];
export type GetDurchgaengeByAkaProduktIdsOutput = RouterOutput['durchgang']['getDurchgaengeByAkaProduktIds'];

export type AkaDurchgangId = GetDurchgaengeByAkaProduktIdsOutput[number]['akaDurchgangId'];

export type GetHealthByDurchgangOutput = RouterOutput['durchgang']['getHealthByDurchgang'];
export type CheckDurchgangAvailabilityInput = RouterInput['durchgang']['checkDurchgangAvailability'];
export type CheckDurchgangAvailabilityOutput = RouterOutput['durchgang']['checkDurchgangAvailability'];
export type CheckDurchgangAvailabilityOutputModulTag = ArrayElement<
  ArrayElement<CheckDurchgangAvailabilityOutput['module']>['tage']
>;

export type Durchgangsart = GetDurchgangByAkaDurchgangIdOutput['durchgangsart'];

export const durchgangsarten = exhaustiveRecord<NonNullable<Durchgangsart>>()({
  STANDARD: 'standard',
  ZUSATZ: 'zusatz',
  ERSATZ: 'ersatz',
});

// planung

export type ExpertePool = PlanungsinformationFromThemaRouter['expertePool'];
export type ExpertePlanung = ArrayElement<ExpertePool>;
export type CservWorkflowStatus = NonNullable<PlanungsinformationFromThemaRouter['cservWorkflowStatus']>;

export type ExperteNameWithGesperrtStatus = { vorname: string; nachname: string; istGesperrt: boolean };

export const cservWorkflowStatus = {
  Produktmanagement: 160,
  VA_ORGA: 161,
  PLANUNG_ABGESCHLOSSEN: 162,
  PLANUNG_INAKTIV: 190,
} as const satisfies Record<string, CservWorkflowStatus>;

export type LocationBesonderheit = NonNullable<PlanungsinformationFromThemaRouter['locationBesonderheiten']>[number];

export const locationBesonderheit = exhaustiveRecord<LocationBesonderheit>()({
  PC_CENTER: 'PC_CENTER',
});

export type CservOnlineTools = NonNullable<PlanungsinformationFromThemaRouter['onlineTool']>;

export const cservOnlineTools = exhaustiveRecord<CservOnlineTools>()({
  MS_TEAMS: 'ms_teams',
  LUX: 'LUX',
  TECHCAST: 'techcast',
  TRICAT: 'tricat',
  ZOOM: 'zoom',
});

export type UpdateStatusOfVeranstaltungenInput = RouterInput['planung']['updateStatusOfVeranstaltungen'];
export type UpdateStatusOfVeranstaltungenOutput = RouterOutput['planung']['updateStatusOfVeranstaltungen'];

export type StatusChangeResult = ArrayElement<UpdateStatusOfVeranstaltungenOutput>;

// stateMachine
export type Capability = Veranstaltung['capabilities'][number];
export const capabilities = exhaustiveRecord<Capability>()({
  EDIT_ABLAUF: 'editAblauf',
  EDIT_LOCATION: 'editLocation',
  CHANGE_STANDORT: 'changeStandort',
  SELECT_STANDORT: 'selectStandort',
  EDIT_EXPERTE: 'editExperte',
  DELETE: 'delete',
});

// resources
export type GetAvailableStandorteByRegionInput = RouterInput['planung']['resources']['getAvailableStandorteByRegion'];
export type GetAvailableStandorteByRegionOutput = RouterOutput['planung']['resources']['getAvailableStandorteByRegion'];
export type GetAvailableLocationsInput = RouterInput['planung']['resources']['getAvailableLocations'];
export type GetAvailableLocationsOutput = RouterOutput['planung']['resources']['getAvailableLocations'];
export type GetAvailableRegionenInput = RouterInput['planung']['resources']['getAvailableRegionen'];
export type GetAvailableRegionenOutput = RouterOutput['planung']['resources']['getAvailableRegionen'];

export type GetLocationsWithRaeumeInput = RouterInput['planung']['resources']['getLocationsWithRaeume'];
export type GetLocationsWithRaeumeOutput = RouterOutput['planung']['resources']['getLocationsWithRaeume'];

export type Location = GetLocationsWithRaeumeOutput[number];
export type Raum = Location['seminarraeume'][number];

export type GekaufterZeitraum = Raum['gekaufterZeitraum'];
export type GekaufterZeitraumTag = ArrayElement<GekaufterZeitraum>;

export type LocationArt = NonNullable<Location['art']>;

export const locationArt = exhaustiveRecord<LocationArt>()({
  HOTEL: 'HOTEL',
  VERANSTALTUNGSLOCATION: 'VERANSTALTUNGSLOCATION',
  PC_CENTER: 'PC_CENTER',
});
export const locationArtSchema = z.enum(locationArt);

export type GetBookableExpertenByThemaInput = RouterInput['planung']['resources']['getBookableExpertenByThema'];
export type GetBookableExpertenByThemaOutput = RouterOutput['planung']['resources']['getBookableExpertenByThema'];

export type ExperteFromResourcesRouter = ArrayElement<GetBookableExpertenByThemaOutput>;

export type ExperteBlockungQuelle = ArrayElement<ExperteFromResourcesRouter['experteBlockungen']>['quelle'];

export const experteBlockungQuelle = {
  SAP: 'AKA.BAN.SAP',
  VAMOS: 'AKA.BAN.VMOS',
  CESAR: 'AKA.BAN.CESAR.SALESFORCE',
} as const satisfies Record<string, ExperteBlockungQuelle>;

export type GetStandorteInput = RouterInput['planung']['resources']['getStandorte'];
export type GetStandorteOutput = RouterOutput['planung']['resources']['getStandorte'];

export type Standort = ArrayElement<GetStandorteOutput>;

export type GetExperteByNamenOrSapIdInput = RouterInput['planung']['resources']['getExperteByNamenOrSapId'];
export type GetExperteByNamenOrSapIdOutput = RouterOutput['planung']['resources']['getExperteByNamenOrSapId'];

export type SelectedExperte = ArrayElement<GetExperteByNamenOrSapIdOutput>;

// fehler
export type GetFachlicheFehlerOutput = RouterOutput['fehler']['getFachlicheFehler'];
export type FachlicherFehler = ArrayElement<GetFachlicheFehlerOutput>;

/*
 * Scopes
 */

export const vivaUserScopes = {
  PLANUNG_EDIT: 'planung:edit',
  THEMA_EDIT: 'thema:edit',
  SYSTEM_ADMIN: 'system:admin',
} as const;

const vivaUserScopesSchema = z.enum(vivaUserScopes);
export const vivaUserScopesArraySchema = z.array(vivaUserScopesSchema);
export type VivaUserScope = z.infer<typeof vivaUserScopesSchema>;
